import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { ColumnVisibility } from '@/components/common/ColumnVisibility/ColumnVisibility';
import { useColumnVisibility } from '@/components/common/ColumnVisibility/useColumnVisibility';
import { advanceFiltersNormalizer } from '@/helpers/advanceFilters/advanceFiltersNormalizer.helper';
import { useModals } from '@/router';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import { AdvertiserList } from '../AdvertiserList';
import {
  ADVERTISER_COLUMN_VISIBILITY_KEY,
  ADVERTISER_HIDEABLE_COLUMN_IDS,
  useAdvertiserColumnLabels
} from '../AdvertiserList/advertiserColumns';
import EntityTabEmptyState from '../EntityTabEmptyState/EntityTabEmptyState';
import { ListFilters } from '../ListFilters';
import { DateRange } from '../ListFilters/DateRange';
import { getColumnVisibilityOptions } from '../Shared/columnVisibilityOptions';

export default function AdvertiserTab() {
  const { applyVisibleColumns, isVisible: isColumnVisible } = useColumnVisibility(
    ADVERTISER_COLUMN_VISIBILITY_KEY,
    ADVERTISER_HIDEABLE_COLUMN_IDS
  );
  const columnLabels = useAdvertiserColumnLabels();
  const { formatMessage } = useIntl();
  const modals = useModals();
  const location = useLocation();
  const {
    advertiserListFilters,
    cacheSeed,
    hasAdvertisers,
    isLoadingCountLineItems,
    isLoadingGetAdvertisers,
    isLoadingGetCampaigns,
    isLoadingGetInsertionOrders,
    setAdvertiserListFilters,
    setCacheSeed
  } = useCampaignStore() as CampaignsStore;

  const isLoading =
    isLoadingCountLineItems || isLoadingGetAdvertisers || isLoadingGetCampaigns || isLoadingGetInsertionOrders;

  const normalizedFilters = useMemo(() => {
    let filters: Record<string, any> = {};
    if (advertiserListFilters.text) {
      filters.name = encodeURIComponent(advertiserListFilters.text);
    }
    filters = advanceFiltersNormalizer(advertiserListFilters.advanceFilters, filters);
    return filters;
  }, [advertiserListFilters.text, advertiserListFilters.advanceFilters]);

  if (!hasAdvertisers && !isLoading) {
    return (
      <EntityTabEmptyState
        buttonProps={{
          onClick: () =>
            modals.open('/campaigns/new/advertiserModal', {
              at: { pathname: '/campaigns', search: location.search },
              state: { advertiser: {}, modalId: 'advertiser-modal' }
            }),
          text: formatMessage({
            defaultMessage: 'Create Advertiser',
            id: 'advertisers.emptyState.createButton'
          })
        }}
        description={formatMessage({
          defaultMessage: 'Create your first Advertiser',
          id: 'advertisers.emptyState.description'
        })}
        title={formatMessage({
          defaultMessage: 'Get Started Today!',
          id: 'advertisers.emptyState.title'
        })}
      />
    );
  }

  const leftFilters = [
    {
      className: 'campaignsListsFilters__leftFiltersDateRange',
      component: <DateRange id="tab_advertiser__date_filter" value={undefined} />,
      id: 'advertiserFilters__dateRangeFilter',
      sendCallback: true
    }
  ];

  const handleFilter = (newFilters: Record<string, any>) => {
    setAdvertiserListFilters(newFilters);
    setCacheSeed();
  };

  const handleListChange = () => {
    setCacheSeed();
  };

  return (
    <>
      <ListFilters
        extraActions={
          <ColumnVisibility
            columns={getColumnVisibilityOptions(columnLabels, ADVERTISER_HIDEABLE_COLUMN_IDS, isColumnVisible)}
            onApply={applyVisibleColumns}
            prefixId="advertiserTab"
          />
        }
        initialFilters={advertiserListFilters}
        leftFilters={leftFilters}
        onFilter={handleFilter}
        prefixId="advertiserTab"
      />
      <AdvertiserList
        cacheSeed={cacheSeed}
        filters={normalizedFilters}
        isColumnVisible={isColumnVisible}
        onListChange={handleListChange}
      />
    </>
  );
}
